import React, { useState, useEffect, useCallback } from 'react';
import { collection, query, orderBy, limit, getDocs, doc, getDoc, setDoc } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../utils/firebase';
import { collectionPath } from '../utils/firestoreCollections';
import { useAuth } from '../components/auth/AuthContext';
import { canViewAllData, canModifyData, hasPermission, FEATURE_PERMISSIONS } from '../utils/permissions';
import { logUserAction } from '../utils/logger';
import { exportData } from '../utils/exportUtils';
import { sanitizeString } from '../utils/sanitization';

// Sync settings keys mapped to Firestore systemSettings doc IDs
const SYNC_SETTINGS = {
  customer: {
    quick: { key: 'customerSyncLookbackDays', label: 'Quick Sync Lookback', default: 14, unit: 'days' },
    full: { key: 'customerSyncCursorRewindFull', label: 'Full Sync Re-scan', default: 5000, unit: 'records' },
  },
  order: {
    quick: { key: 'orderSyncPhase2LookbackDays', label: 'Quick Sync Lookback', default: 31, unit: 'days' },
    full: { key: 'orderSyncCursorRewindFull', label: 'Full Sync Re-scan', default: 15000, unit: 'records' },
  },
  contact: {
    quick: { key: 'contactSyncLookbackDays', label: 'Quick Sync Lookback', default: 14, unit: 'days' },
    full: { key: 'contactSyncLookbackDaysFull', label: 'Full Sync Lookback', default: 365, unit: 'days' },
  },
  // user: no configurable settings — user sync always fetches all users from AM
};

const SYNC_LOG_COLLECTIONS = {
  customer: 'customersSyncLogs',
  order: 'ordersSyncLogs',
  contact: 'contactsSyncLogs',
  user: 'usersSyncLogs',
  typesense: 'typesenseSyncLogs',
  product: 'productsSyncLogs',
};

// Each sync script has historically used entity-specific field names; this maps
// any log (old or new) onto one shape for display and export.
export function normalizeSyncLog(log) {
  if (!log) return null;
  const firstDefined = (...vals) => vals.find(v => v !== undefined && v !== null && v !== '');

  const createdIds = firstDefined(
    log.createdIds, log.createdCustomerIds, log.createdOrderIds, log.createdContactIds
  ) || [];

  const updatedDetails = (log.updatedDetails || []).map(u => ({
    id: firstDefined(u.id, u.customerId, u.orderId, u.contactId, u.userId) ?? '',
    name: firstDefined(u.name, u.customerName, u.orderNumber, u.contactName, u.userName) ?? '',
    changedFields: (u.changedFields || []).map(f => (typeof f === 'string' ? f : f?.field)).filter(Boolean),
  }));

  const lookback = log.cursorRewind
    ? `${log.cursorRewind} records`
    : firstDefined(log.lookbackDays, log.orderSyncPhase2LookbackDays)
      ? `${firstDefined(log.lookbackDays, log.orderSyncPhase2LookbackDays)} days`
      : null;

  return {
    totalProcessed: firstDefined(
      log.totalCustomersProcessed, log.totalOrdersProcessed, log.totalFetched, log.contactsTotal
    ),
    // 0 is a real value (e.g. quick order sync with no new pages), so don't treat it as missing.
    pages: log.totalPages ?? null,
    lookback,
    skipped: firstDefined(log.stats?.skippedNoSalesRep, log.usersSkipped) ?? 0,
    cursorStart: firstDefined(log.startCustomerId, log.startOrderId, log.startContactId),
    cursorEnd: firstDefined(log.endCustomerId, log.endOrderId, log.endContactId, log.lastUserId),
    createdIds,
    createdIdsTotal: log.createdIdsTotal ?? createdIds.length,
    updatedDetails,
    updatedDetailsTotal: log.updatedDetailsTotal ?? updatedDetails.length,
    detailsTruncated: log.detailsTruncated === true,
    // Legacy logs recorded counts but not the per-record lists.
    detailsRecorded: Array.isArray(log.updatedDetails) || createdIds.length > 0,
  };
}

export default function DataSyncPage() {
  const { currentUser } = useAuth();
  const [syncLogs, setSyncLogs] = useState({ customer: null, order: null, contact: null, user: null, typesense: null, product: null });
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState({ customer: false, order: false, contact: false, user: false, typesense: false, product: false });
  const [runningJobs, setRunningJobs] = useState({});
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [confirmHistorical, setConfirmHistorical] = useState(null);
  const [confirmFullSync, setConfirmFullSync] = useState(null);
  const [fullSyncStartId, setFullSyncStartId] = useState('');
  const [cursorPositions, setCursorPositions] = useState({ customer: null, order: null, contact: null, user: null });
  const [detailModal, setDetailModal] = useState(null);
  const [historyModal, setHistoryModal] = useState(null);
  const [historyLogs, setHistoryLogs] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  const functions = getFunctions();
  const triggerVpsSyncFn = httpsCallable(functions, 'triggerVpsSync');
  const getSyncStatusFn = httpsCallable(functions, 'getSyncStatus');
  const cancelVpsSyncFn = httpsCallable(functions, 'cancelVpsSync');

  const canTriggerSync = hasPermission(currentUser, FEATURE_PERMISSIONS.DATA_SYNC_TRIGGER);
  const canTriggerHistorical = hasPermission(currentUser, FEATURE_PERMISSIONS.DATA_SYNC_HISTORICAL);
  const canViewSync = hasPermission(currentUser, FEATURE_PERMISSIONS.DATA_SYNC_VIEW);

  // ── Sync Settings State ──────────────────────────────────
  const [showSettings, setShowSettings] = useState(false);
  const [syncSettings, setSyncSettings] = useState({});
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsDirty, setSettingsDirty] = useState(false);

  const loadSyncSettings = useCallback(async () => {
    setSettingsLoading(true);
    try {
      const settings = {};
      for (const [entity, modes] of Object.entries(SYNC_SETTINGS)) {
        settings[entity] = {};
        for (const [mode, config] of Object.entries(modes)) {
          const docRef = doc(db, collectionPath('systemSettings'), config.key);
          const snap = await getDoc(docRef);
          settings[entity][mode] = snap.exists() && snap.data().value !== undefined
            ? snap.data().value
            : config.default;
        }
      }
      setSyncSettings(settings);
      setSettingsDirty(false);
    } catch (err) {
      console.error('Failed to load sync settings:', err);
    } finally {
      setSettingsLoading(false);
    }
  }, []);

  const saveSyncSettings = async () => {
    setSettingsSaving(true);
    try {
      for (const [entity, modes] of Object.entries(SYNC_SETTINGS)) {
        for (const [mode, config] of Object.entries(modes)) {
          const val = Number(syncSettings[entity]?.[mode]);
          if (isNaN(val) || val < 1 || val > 99999) continue;
          const docRef = doc(db, collectionPath('systemSettings'), config.key);
          await setDoc(docRef, {
            value: val,
            category: 'config',
            isActive: true,
            label: `${entity.charAt(0).toUpperCase() + entity.slice(1)} ${config.label}`,
            description: `Lookback period in ${config.unit} for ${entity} ${mode} sync`,
            updatedAt: new Date().toISOString(),
            updatedBy: currentUser?.email || 'unknown',
          }, { merge: true });
        }
      }
      await logUserAction({
        action: 'Sync Settings Updated',
        details: `Updated sync lookback settings: ${JSON.stringify(syncSettings)}`,
        entityType: 'DataSync',
        entityId: 'settings',
        level: 'info',
      });
      setSettingsDirty(false);
      setSuccessMessage('Sync settings saved successfully.');
    } catch (err) {
      setError('Failed to save sync settings: ' + err.message);
    } finally {
      setSettingsSaving(false);
    }
  };

  const updateSetting = (entity, mode, value) => {
    const numVal = value === '' ? '' : Number(value);
    setSyncSettings(prev => ({ ...prev, [entity]: { ...prev[entity], [mode]: numVal } }));
    setSettingsDirty(true);
  };

  // Fetch cursor positions from the latest successful sync log in each collection
  const fetchCursorPositions = useCallback(async () => {
    try {
      const CURSOR_FIELDS = {
        order: 'endOrderId',
        customer: 'endCustomerId',
        contact: 'endContactId',
        user: 'lastUserId',
      };
      const positions = {};
      for (const [entity, field] of Object.entries(CURSOR_FIELDS)) {
        const colName = SYNC_LOG_COLLECTIONS[entity];
        const ref = collection(db, collectionPath(colName));
        const q = query(ref, orderBy('timestamp', 'desc'), limit(10));
        const snap = await getDocs(q);
        // Find the latest successful log with a cursor value
        const successLog = snap.docs.find(d => {
          const data = d.data();
          return data.status === 'success' && data[field];
        });
        positions[entity] = successLog ? (Number(successLog.data()[field]) || null) : null;
      }
      setCursorPositions(positions);
    } catch (err) {
      console.debug('Could not fetch cursor positions:', err.message);
    }
  }, []);

  useEffect(() => {
    fetchSyncLogs();
    checkSyncStatus();
    loadSyncSettings();
    fetchCursorPositions();
    const interval = setInterval(checkSyncStatus, 10000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (successMessage) {
      const t = setTimeout(() => setSuccessMessage(null), 5000);
      return () => clearTimeout(t);
    }
  }, [successMessage]);

  const checkSyncStatus = async () => {
    try {
      const result = await getSyncStatusFn();
      setRunningJobs(result.data?.jobs || {});
    } catch (err) {
      console.debug('Could not check sync status:', err.message);
    }
  };

  const fetchSyncLogs = async () => {
    setLoading(true);
    setError(null);
    const logs = {};
    let firstError = null;
    for (const [entity, colName] of Object.entries(SYNC_LOG_COLLECTIONS)) {
      try {
        const ref = collection(db, collectionPath(colName));
        const q = query(ref, orderBy('timestamp', 'desc'), limit(1));
        const snap = await getDocs(q);
        if (!snap.empty) {
          logs[entity] = { id: snap.docs[0].id, ...snap.docs[0].data() };
        }
      } catch (err) {
        console.error(`Error fetching ${entity} sync logs:`, err);
        if (!firstError) firstError = err;
      }
    }
    setSyncLogs(logs);
    if (firstError && Object.keys(logs).length === 0) {
      setError(firstError.message);
    }
    setLoading(false);
  };

  const fetchHistory = async (entityType) => {
    setHistoryModal(entityType);
    setHistoryLoading(true);
    try {
      const ref = collection(db, collectionPath(SYNC_LOG_COLLECTIONS[entityType]));
      const q = query(ref, orderBy('timestamp', 'desc'), limit(25));
      const snap = await getDocs(q);
      setHistoryLogs(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error('Error fetching history:', err);
    } finally {
      setHistoryLoading(false);
    }
  };

  const triggerSync = async (entityType, syncMode = 'quick', options = {}) => {
    setSyncing(prev => ({ ...prev, [entityType]: true }));
    setError(null);
    setSuccessMessage(null);

    try {
      const payload = { entityType, syncMode, sendNotification: true };
      if (options.startId) payload.startId = Number(options.startId);
      const result = await triggerVpsSyncFn(payload);

      await logUserAction({
        action: 'Data Sync Triggered',
        details: `${entityType.charAt(0).toUpperCase() + entityType.slice(1)} ${syncMode} sync triggered`,
        entityType: 'DataSync',
        entityId: entityType,
        level: 'info',
      });

      if (result.data?.status === 'started') {
        const modeLabel = syncMode === 'historical' ? 'Historical' : syncMode === 'full' ? 'Full' : 'Quick';
        setSuccessMessage(`${entityType.charAt(0).toUpperCase() + entityType.slice(1)} ${modeLabel} sync started! You'll receive an email notification when complete.`);

        setRunningJobs(prev => ({
          ...prev,
          [entityType]: { entityType, syncMode, status: 'running', startTime: new Date().toISOString() },
        }));

        const pollInterval = setInterval(async () => {
          try {
            const currentStatus = await getSyncStatusFn();
            const jobs = currentStatus.data?.jobs || {};
            setRunningJobs(jobs);
            if (!jobs[entityType]) {
              clearInterval(pollInterval);
              await fetchSyncLogs();
              fetchCursorPositions();
            }
          } catch { /* ignore poll errors */ }
        }, 5000);

        setTimeout(() => { clearInterval(pollInterval); fetchSyncLogs(); fetchCursorPositions(); }, 600000);
      }
    } catch (err) {
      console.error('Error triggering sync:', err);
      if (err.code === 'functions/failed-precondition') {
        setError('Sync server not configured. Set up VPS_SYNC_URL and VPS_SYNC_API_KEY in Firebase Functions config.');
      } else if (err.code === 'functions/permission-denied') {
        setError('You do not have permission to trigger syncs.');
      } else {
        setError(err.message || 'Failed to trigger sync');
      }
    } finally {
      setSyncing(prev => ({ ...prev, [entityType]: false }));
    }
  };

  const cancelSync = async (entityType) => {
    setError(null);
    try {
      const result = await cancelVpsSyncFn({ entityType });
      await logUserAction({
        action: 'Data Sync Cancelled',
        details: `${entityType.charAt(0).toUpperCase() + entityType.slice(1)} sync cancelled`,
        entityType: 'DataSync',
        entityId: entityType,
        level: 'warning',
      });
      setRunningJobs(prev => {
        const next = { ...prev };
        delete next[entityType];
        return next;
      });
      setSuccessMessage(result.data?.message || `${entityType} sync cancelled.`);
      await fetchSyncLogs();
    } catch (err) {
      console.error('Error cancelling sync:', err);
      setError(err.message || 'Failed to cancel sync');
    }
  };

  // ── Export helpers ──────────────────────────────────

  const exportSyncLog = (entityType, log, format = 'csv') => {
    if (!log) return;
    const stats = log.stats || {};
    const n = normalizeSyncLog(log);
    const naIfMissing = (v) => (v === undefined || v === null ? 'N/A' : v);

    const headers = ['Field', 'Value'];
    const summaryData = [
      { f: 'Entity Type', v: entityType },
      { f: 'Run ID', v: log.id },
      { f: 'Status', v: log.status },
      { f: 'Timestamp', v: formatTimestamp(log.timestamp) },
      { f: 'Duration', v: formatDuration(log.durationMs || log.duration) },
      { f: 'Total Processed', v: naIfMissing(n.totalProcessed) },
      { f: 'Created', v: stats.created || 0 },
      { f: 'Updated', v: stats.updated || 0 },
      { f: 'Unchanged', v: stats.unchanged || 0 },
      { f: 'Skipped', v: n.skipped },
      { f: 'Pages', v: naIfMissing(n.pages) },
      { f: 'Lookback / Re-scan', v: naIfMissing(n.lookback) },
      { f: 'Cursor Start', v: naIfMissing(n.cursorStart) },
      { f: 'Cursor End', v: naIfMissing(n.cursorEnd) },
      { f: '', v: '' },
      { f: `--- Created IDs (${n.createdIds.length}) ---`, v: '' },
      ...(n.createdIds.length
        ? n.createdIds.map(id => ({ f: 'Created', v: id }))
        : [{ f: n.detailsRecorded ? '(none)' : '(not recorded for this run)', v: '' }]),
      { f: '', v: '' },
      { f: `--- Updated Details (${n.updatedDetails.length}) ---`, v: '' },
      ...(n.updatedDetails.length
        ? n.updatedDetails.map(u => ({
            f: u.name ? `${u.id} (${u.name})` : String(u.id),
            v: u.changedFields.join(', '),
          }))
        : [{ f: n.detailsRecorded ? '(none)' : '(not recorded for this run)', v: '' }]),
    ];

    exportData({
      filename: `sync_${entityType}_${log.id || 'latest'}`,
      format,
      headers,
      data: summaryData,
      rowMapper: (row) => [row.f, String(row.v)],
    });

    logUserAction({
      action: 'Data Sync Log Exported',
      details: `Exported ${entityType} sync log (${log.id}) as ${format.toUpperCase()}`,
      entityType: 'DataSync',
      entityId: entityType,
      level: 'info',
    });
  };

  const exportHistory = (entityType, logs, format = 'csv') => {
    if (!logs.length) return;
    const headers = ['Run ID', 'Status', 'Timestamp', 'Duration', 'Created', 'Updated', 'Unchanged', 'Skipped', 'Total Processed', 'Error'];
    exportData({
      filename: `sync_history_${entityType}`,
      format,
      headers,
      data: logs,
      rowMapper: (l) => [
        l.id, l.status, formatTimestamp(l.timestamp),
        formatDuration(l.durationMs || l.duration),
        l.stats?.created || 0, l.stats?.updated || 0,
        l.stats?.unchanged || 0, normalizeSyncLog(l).skipped,
        normalizeSyncLog(l).totalProcessed ?? '', l.error || '',
      ],
    });
  };

  // ── Formatters ──────────────────────────────────

  const formatTimestamp = (timestamp, includeDate = true) => {
    if (!timestamp) return 'N/A';
    try {
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      if (Number.isNaN(date.getTime())) return String(timestamp);

      const timeZone = currentUser?.timeZone || Intl.DateTimeFormat().resolvedOptions().timeZone;
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).formatToParts(date).reduce((result, part) => {
        if (part.type !== 'literal') result[part.type] = part.value;
        return result;
      }, {});
      const dateFormat = currentUser?.dateFormat || 'MM/DD/YYYY';
      const dateString = dateFormat === 'DD/MM/YYYY'
        ? `${parts.day}/${parts.month}/${parts.year}`
        : dateFormat === 'MM-DD-YYYY'
          ? `${parts.month}-${parts.day}-${parts.year}`
          : dateFormat === 'YYYY-MM-DD'
            ? `${parts.year}-${parts.month}-${parts.day}`
            : `${parts.month}/${parts.day}/${parts.year}`;
      const timeString = new Intl.DateTimeFormat('en-US', {
        timeZone,
        hour: 'numeric',
        minute: '2-digit',
        second: '2-digit',
      }).format(date);

      return includeDate ? `${dateString}, ${timeString}` : timeString;
    } catch { return String(timestamp); }
  };

  const formatDuration = (ms) => {
    if (!ms) return 'N/A';
    const seconds = Math.floor(ms / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    if (hours > 0) return `${hours}h ${minutes % 60}m`;
    if (minutes > 0) return `${minutes}m ${seconds % 60}s`;
    return `${seconds}s`;
  };

  // ── Sync Card Component ──────────────────────────────────

  const SyncCard = ({ entityType, log, isSyncing, runningJob }) => {
    const stats = log?.stats || {};
    const entityDisplay = entityType.charAt(0).toUpperCase() + entityType.slice(1);
    const isSuccess = log?.status === 'success';
    const isCrashed = log?.status === 'crashed';
    const isCancelled = log?.status === 'cancelled';
    const syncTypeLabel = log?.syncMode === 'historical' ? 'Historical' : log?.syncMode === 'full' ? 'Full' : 'Quick';
    const isRunning = !!runningJob;

    return (
      <div className={`bg-white rounded-lg shadow-sm border p-6 ${isRunning ? 'border-blue-400 ring-2 ring-blue-100' : 'border-gray-200'}`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <span className={`text-xl ${isRunning ? 'animate-pulse' : ''}`}>🗄️</span>
            <h3 className="text-lg font-semibold text-gray-900">{entityDisplay} Sync</h3>
          </div>
          {isRunning ? (
            <div className="flex items-center gap-2">
              <span className="inline-block w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
              <span className="text-sm font-medium text-blue-600">Running...</span>
            </div>
          ) : log && (
            <span className={`text-sm font-medium ${isSuccess ? 'text-green-600' : isCancelled ? 'text-yellow-600' : 'text-red-600'}`}>
              {isSuccess ? '✅ Success' : isCrashed ? '💥 Crashed' : isCancelled ? '⏹ Cancelled' : '❌ Failed'}
            </span>
          )}
        </div>

        {isRunning && (
          <div className="mb-4 bg-blue-50 border border-blue-200 rounded p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-blue-800">
                  <strong>{runningJob.syncMode === 'historical' ? 'Historical' : runningJob.syncMode === 'full' ? 'Full' : 'Quick'} sync in progress...</strong>
                </p>
                <p className="text-xs text-blue-600 mt-1">
                  Started: {formatTimestamp(runningJob.startTime, false)}
                  {runningJob.triggeredBy && ` by ${runningJob.triggeredBy}`}
                </p>
              </div>
              {canTriggerSync && (
                <button
                  onClick={() => cancelSync(entityType)}
                  className="px-3 py-1.5 bg-red-600 text-white text-xs font-medium rounded hover:bg-red-700 transition-colors"
                  title="Stop the running sync"
                >
                  ⏹ Stop
                </button>
              )}
            </div>
          </div>
        )}

        {log ? (
          <div className="space-y-3 mb-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Last Run:</span>
                <p className="font-medium text-gray-900">{formatTimestamp(log.timestamp)}</p>
              </div>
              <div>
                <span className="text-gray-500">Type:</span>
                <p className="font-medium text-gray-900">{syncTypeLabel}</p>
              </div>
              <div>
                <span className="text-gray-500">Duration:</span>
                <p className="font-medium text-gray-900">{formatDuration(log.durationMs || log.duration)}</p>
              </div>
              <div>
                <span className="text-gray-500">Run ID:</span>
                <p className="font-medium text-gray-900 truncate" title={log.id}>{log.id}</p>
              </div>
            </div>

            {isSuccess && (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-3 border-t">
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">{stats.created || 0}</p>
                  <p className="text-xs text-gray-500">Created</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-blue-600">{stats.updated || 0}</p>
                  <p className="text-xs text-gray-500">Updated</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-gray-600">{stats.unchanged || 0}</p>
                  <p className="text-xs text-gray-500">Unchanged</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-bold text-yellow-600">{stats.skippedNoSalesRep || 0}</p>
                  <p className="text-xs text-gray-500">Skipped</p>
                </div>
              </div>
            )}

            {!isSuccess && log.error && !isRunning && (
              <div className="bg-red-50 border border-red-200 rounded p-3">
                <p className="text-sm text-red-800">{log.error}</p>
              </div>
            )}

            <div className="flex gap-3 pt-2 text-xs">
              <button onClick={() => setDetailModal({ entityType, log })} className="text-blue-600 hover:underline">
                📋 View Details
              </button>
              <button onClick={() => fetchHistory(entityType)} className="text-blue-600 hover:underline">
                📜 Sync History
              </button>
              <div className="relative group">
                <button className="text-blue-600 hover:underline">⬇️ Export</button>
                <div className="hidden group-hover:block absolute left-0 top-full mt-1 bg-white border rounded shadow-lg z-10 min-w-[100px]">
                  <button onClick={() => exportSyncLog(entityType, log, 'csv')} className="block w-full text-left px-3 py-1.5 hover:bg-gray-100 text-xs">CSV</button>
                  <button onClick={() => exportSyncLog(entityType, log, 'xlsx')} className="block w-full text-left px-3 py-1.5 hover:bg-gray-100 text-xs">Excel</button>
                </div>
              </div>
            </div>
          </div>
        ) : !isRunning && (
          <div className="text-center py-8 text-gray-400">
            <p className="text-3xl mb-2 opacity-50">📭</p>
            <p className="text-sm">No sync history found</p>
          </div>
        )}

        {canTriggerSync && (
          <div className="flex gap-2 pt-4 border-t">
            <button
              onClick={() => triggerSync(entityType, 'quick')}
              disabled={isSyncing || isRunning}
              className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors text-sm font-medium"
              title="Start quick incremental sync"
            >
              {isSyncing ? <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : '🔄'}
              Quick Sync
            </button>
            {SYNC_SETTINGS[entityType]?.full && (
              <button
                onClick={() => { setConfirmFullSync(entityType); setFullSyncStartId(''); }}
                disabled={isSyncing || isRunning}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-purple-600 text-white rounded hover:bg-purple-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                title="Full sync — re-scan recent records, diff-based"
              >
                {isSyncing ? <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : '🗄️'}
                Full Sync
              </button>
            )}
            {canTriggerHistorical && SYNC_SETTINGS[entityType]?.full && (
              <button
                onClick={() => setConfirmHistorical(entityType)}
                disabled={isSyncing || isRunning}
                className="flex items-center justify-center gap-2 px-3 py-2 bg-amber-600 text-white rounded hover:bg-amber-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
                title="Historical sync — re-writes ALL records from scratch"
              >
                ⚠️
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  // ── Typesense Sync Card ──────────────────────────────────

  const TypesenseCard = ({ log, isSyncing, runningJob }) => {
    const isRunning = !!runningJob;
    const isSuccess = log?.status === 'success';
    const isPartial = log?.status === 'partial';
    const detail = log?.collectionsDetail || {};
    const hasDetail = Object.keys(detail).length > 0;

    return (
      <div className={`bg-white rounded-lg shadow-sm border p-6 ${isRunning ? 'border-indigo-400 ring-2 ring-indigo-100' : 'border-gray-200'}`}>
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <span className={`text-xl ${isRunning ? 'animate-pulse' : ''}`}>🔍</span>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">Search Index</h3>
              <p className="text-xs text-gray-500">Typesense — full rebuild from Firestore</p>
            </div>
          </div>
          {isRunning ? (
            <div className="flex items-center gap-2">
              <span className="inline-block w-4 h-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
              <span className="text-sm font-medium text-indigo-600">Running...</span>
            </div>
          ) : log && (
            <span className={`text-sm font-medium ${isSuccess ? 'text-green-600' : isPartial ? 'text-yellow-600' : 'text-red-600'}`}>
              {isSuccess ? '✅ Success' : isPartial ? '⚠️ Partial' : '❌ Failed'}
            </span>
          )}
        </div>

        {isRunning && (
          <div className="mb-4 bg-indigo-50 border border-indigo-200 rounded p-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-indigo-800"><strong>Index rebuild in progress...</strong></p>
                <p className="text-xs text-indigo-600 mt-1">
                  Started: {formatTimestamp(runningJob.startTime, false)}
                  {runningJob.triggeredBy && ` by ${runningJob.triggeredBy}`}
                </p>
              </div>
              {canTriggerSync && (
                <button
                  onClick={() => cancelSync('typesense')}
                  className="px-3 py-1.5 bg-red-600 text-white text-xs font-medium rounded hover:bg-red-700 transition-colors"
                  title="Stop the rebuild"
                >
                  ⏹ Stop
                </button>
              )}
            </div>
          </div>
        )}

        {log ? (
          <div className="space-y-3 mb-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Last Run:</span>
                <p className="font-medium text-gray-900">{formatTimestamp(log.timestamp)}</p>
              </div>
              <div>
                <span className="text-gray-500">Duration:</span>
                <p className="font-medium text-gray-900">{formatDuration(log.durationMs)}</p>
              </div>
            </div>

            {(isSuccess || isPartial) && log.stats && (
              <div className="grid grid-cols-2 gap-3 pt-3 border-t">
                <div className="text-center">
                  <p className="text-2xl font-bold text-green-600">{(log.stats.imported || 0).toLocaleString()}</p>
                  <p className="text-xs text-gray-500">Indexed</p>
                </div>
                <div className="text-center">
                  <p className={`text-2xl font-bold ${(log.stats.failed || 0) > 0 ? 'text-red-600' : 'text-gray-400'}`}>{log.stats.failed || 0}</p>
                  <p className="text-xs text-gray-500">Failed</p>
                </div>
              </div>
            )}

            {(isSuccess || isPartial) && hasDetail && (
              <div className="pt-2 border-t">
                <p className="text-xs text-gray-500 mb-2">Per Collection:</p>
                <div className="space-y-1">
                  {Object.entries(detail).map(([col, res]) => (
                    <div key={col} className="flex items-center justify-between text-xs">
                      <span className="text-gray-700 capitalize">{col}</span>
                      {res.error ? (
                        <span className="text-red-600">❌ {res.error}</span>
                      ) : (
                        <span className="text-gray-600">{(res.imported || 0).toLocaleString()} indexed</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {!isSuccess && !isPartial && log.error && !isRunning && (
              <div className="bg-red-50 border border-red-200 rounded p-3">
                <p className="text-sm text-red-800">{log.error}</p>
              </div>
            )}

            <div className="flex gap-3 pt-2 text-xs">
              <button onClick={() => fetchHistory('typesense')} className="text-blue-600 hover:underline">
                📜 Sync History
              </button>
            </div>
          </div>
        ) : !isRunning && (
          <div className="text-center py-8 text-gray-400">
            <p className="text-3xl mb-2 opacity-50">🔍</p>
            <p className="text-sm">No sync history found</p>
          </div>
        )}

        {canTriggerSync && (
          <div className="pt-4 border-t">
            <button
              onClick={() => triggerSync('typesense', 'quick')}
              disabled={isSyncing || isRunning}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors text-sm font-medium"
              title="Rebuild all Typesense search indexes from Firestore data"
            >
              {isSyncing ? <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : '🔍'}
              Rebuild Index
            </button>
            <p className="text-xs text-gray-400 text-center mt-2">Rebuilds all search collections from Firestore</p>
          </div>
        )}
      </div>
    );
  };

  // ── Product Catalog Sync Card ──────────────────────────────

  const ProductCatalogCard = ({ log, isSyncing, runningJob }) => {
    const isRunning = !!runningJob;
    const isSuccess = log?.status === 'success';
    const isPartial = log?.status === 'partial';
    const classes = log?.classCounts || {};
    const classTiles = [
      { key: 'all_season_replenishable', label: 'Reorderable', color: 'text-green-600' },
      { key: 'in_season_limited', label: 'This season', color: 'text-amber-600' },
      { key: 'retired', label: 'Retired', color: 'text-gray-500' },
    ];

    return (
      <div className={`bg-white rounded-lg shadow-sm border p-6 ${isRunning ? 'border-indigo-400 ring-2 ring-indigo-100' : 'border-gray-200'}`}>
        <div className="flex items-center justify-between mb-4 gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <span className={`text-xl ${isRunning ? 'animate-pulse' : ''}`}>👕</span>
            <div className="min-w-0">
              <h3 className="text-lg font-semibold text-gray-900">Product Catalog</h3>
              <p className="text-xs text-gray-500">ApparelMagic styles + stock (read-only), classified by season policy</p>
            </div>
          </div>
          {isRunning ? (
            <div className="flex items-center gap-2">
              <span className="inline-block w-4 h-4 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin" />
              <span className="text-sm font-medium text-indigo-600">Running...</span>
            </div>
          ) : log && (
            <span className={`text-sm font-medium ${isSuccess ? 'text-green-600' : isPartial ? 'text-yellow-600' : 'text-red-600'}`}>
              {isSuccess ? '✅ Success' : isPartial ? '⚠️ Partial' : '❌ Failed'}
            </span>
          )}
        </div>

        {isRunning && canTriggerSync && (
          <div className="mb-4 bg-indigo-50 border border-indigo-200 rounded p-3 flex items-center justify-between gap-3">
            <p className="text-sm text-indigo-800">
              <strong>Catalog sync in progress...</strong>{' '}
              <span className="text-xs">Started {formatTimestamp(runningJob.startTime, false)}</span>
            </p>
            <button
              onClick={() => cancelSync('product')}
              className="px-3 py-1.5 bg-red-600 text-white text-xs font-medium rounded hover:bg-red-700 transition-colors"
              title="Stop the catalog sync"
            >
              ⏹ Stop
            </button>
          </div>
        )}

        {log ? (
          <div className="space-y-3 mb-4">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-gray-500">Last Run:</span>
                <p className="font-medium text-gray-900">{formatTimestamp(log.timestamp)}</p>
              </div>
              <div>
                <span className="text-gray-500">Duration:</span>
                <p className="font-medium text-gray-900">{formatDuration(log.durationMs)}</p>
              </div>
              <div>
                <span className="text-gray-500">Styles / SKUs:</span>
                <p className="font-medium text-gray-900">
                  {(log.totalFetched || 0).toLocaleString()} / {(log.skusEmbedded || 0).toLocaleString()}
                </p>
              </div>
            </div>

            {(isSuccess || isPartial) && (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 pt-3 border-t">
                {classTiles.map((tile) => (
                  <div key={tile.key} className="text-center">
                    <p className={`text-2xl font-bold ${tile.color}`}>{(classes[tile.key] || 0).toLocaleString()}</p>
                    <p className="text-xs text-gray-500">{tile.label}</p>
                  </div>
                ))}
              </div>
            )}

            {(isSuccess || isPartial) && (
              <p className="text-xs text-gray-500">
                {log.productsCreated || 0} new · {log.productsUpdated || 0} changed · {log.productsUnchanged || 0} unchanged
                {log.productsRetiredMissing ? ` · ${log.productsRetiredMissing} removed from ApparelMagic` : ''}
                {log.seasonPolicyToday ? ` · season date ${log.seasonPolicyToday}` : ''}
              </p>
            )}

            {!isSuccess && !isPartial && log.error && !isRunning && (
              <div className="bg-red-50 border border-red-200 rounded p-3">
                <p className="text-sm text-red-800">{log.error}</p>
              </div>
            )}

            <div className="flex gap-3 pt-2 text-xs">
              <button onClick={() => fetchHistory('product')} className="text-blue-600 hover:underline" title="Show recent catalog sync runs">
                📜 Sync History
              </button>
            </div>
          </div>
        ) : !isRunning && (
          <div className="text-center py-8 text-gray-400">
            <p className="text-3xl mb-2 opacity-50">👕</p>
            <p className="text-sm">No sync history found</p>
          </div>
        )}

        {canTriggerSync && (
          <div className="pt-4 border-t">
            <button
              onClick={() => triggerSync('product', 'quick')}
              disabled={isSyncing || isRunning}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors text-sm font-medium"
              title="Fetch all styles and stock from ApparelMagic (read-only)"
            >
              {isSyncing ? <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> : '👕'}
              Sync Catalog
            </button>
          </div>
        )}
      </div>
    );
  };

  // ── Access Control ──────────────────────────────────

  if (!canViewAllData(currentUser) && !canViewSync) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <p className="text-3xl mb-4">🚫</p>
          <h2 className="text-xl font-semibold text-red-900 mb-2">Access Denied</h2>
          <p className="text-red-700">You do not have permission to access Data Sync Management.</p>
        </div>
      </div>
    );
  }

  // ── Main Render ──────────────────────────────────

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Data Sync Management</h1>
            <p className="text-gray-600 mt-1">Monitor and trigger synchronization with ApparelMagic API</p>
          </div>
          <button
            onClick={fetchSyncLogs}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-colors disabled:opacity-50"
          >
            {loading ? <span className="inline-block w-4 h-4 border-2 border-gray-500 border-t-transparent rounded-full animate-spin" /> : '🔄'} Refresh
          </button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <span className="flex-shrink-0 mt-0.5">⚠️</span>
          <div>
            <h3 className="font-semibold text-red-900">Error</h3>
            <p className="text-red-700 text-sm">{error}</p>
          </div>
          <button onClick={() => setError(null)} className="ml-auto text-red-600 hover:text-red-800 text-lg">✕</button>
        </div>
      )}

      {/* Success Alert */}
      {successMessage && (
        <div className="mb-6 bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
          <span className="flex-shrink-0 mt-0.5">✅</span>
          <div>
            <h3 className="font-semibold text-green-900">Success</h3>
            <p className="text-green-700 text-sm">{successMessage}</p>
          </div>
          <button onClick={() => setSuccessMessage(null)} className="ml-auto text-green-600 hover:text-green-800 text-lg">✕</button>
        </div>
      )}

      {/* Info Banner */}
      <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <span className="flex-shrink-0 mt-0.5">ℹ️</span>
          <div className="text-sm text-blue-900">
            <p className="font-semibold mb-1">Sync Options</p>
            <ul className="list-disc list-inside space-y-1 text-blue-800">
              <li><strong>Quick Sync:</strong> Resumes from last cursor position, re-checks recent records within the lookback window</li>
              <li><strong>Full Sync:</strong> Rewinds cursor and re-scans a larger window of records (e.g., last 365 days), writes only changes</li>
              <li><strong>Historical Sync (⚠️):</strong> Scans ALL records from the very beginning — use when data integrity is in question</li>
              <li><strong>Automated:</strong> Quick syncs run once per day via VPS cron jobs</li>
            </ul>
          </div>
        </div>
      </div>

      {/* ── Sync Settings ── */}
      <div className="mb-6 bg-white border border-gray-200 rounded-lg shadow-sm">
        <button
          onClick={() => { setShowSettings(!showSettings); if (!showSettings && !Object.keys(syncSettings).length) loadSyncSettings(); }}
          className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 transition-colors rounded-lg"
        >
          <div className="flex items-center gap-2">
            <span>⚙️</span>
            <span className="font-semibold text-gray-900 text-sm">Sync Settings</span>
            <span className="text-xs text-gray-500">(lookback windows for each sync mode)</span>
          </div>
          <span className="text-gray-400 text-sm">{showSettings ? '▲' : '▼'}</span>
        </button>

        {showSettings && (
          <div className="px-4 pb-4 border-t">
            {settingsLoading ? (
              <div className="flex items-center justify-center py-6">
                <span className="inline-block w-5 h-5 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <>
                <p className="text-xs text-gray-500 mt-3 mb-4">
                  These values control how far back each sync mode scans. Quick syncs use a lookback in <strong>days</strong>.
                  For Orders and Customers, full syncs re-scan a number of <strong>records</strong> back from the current cursor position
                  (the ID-based cursor determines the scan range, not dates). For Contacts, full sync uses a date-based lookback.
                  Historical syncs always process all records regardless of these settings.
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {Object.entries(SYNC_SETTINGS).map(([entity, modes]) => {
                    const cursorEnd = cursorPositions[entity];
                    const fullConfig = modes.full;
                    const rewindVal = syncSettings[entity]?.full ?? fullConfig?.default;
                    const isRecords = fullConfig?.unit === 'records';
                    return (
                    <div key={entity} className="bg-gray-50 rounded-lg p-4 border">
                      <h4 className="font-semibold text-gray-900 text-sm mb-3 capitalize">{entity}s</h4>
                      {cursorEnd && isRecords && (
                        <div className="text-xs text-gray-500 bg-white rounded p-2 mb-3 border border-gray-200">
                          📍 Current cursor: <strong className="text-gray-700">#{cursorEnd}</strong>
                          {rewindVal && <span> · Full re-scan covers <strong className="text-gray-700">#{Math.max(1, cursorEnd - rewindVal)} → #{cursorEnd}</strong></span>}
                        </div>
                      )}
                      {Object.entries(modes).map(([mode, config]) => (
                        <div key={mode} className="mb-3 last:mb-0">
                          <label className="block text-xs text-gray-600 mb-1">{config.label}</label>
                          <div className="flex items-center gap-2">
                            <input
                              type="number"
                              min="1"
                              max="99999"
                              value={syncSettings[entity]?.[mode] ?? config.default}
                              onChange={(e) => updateSetting(entity, mode, e.target.value)}
                              disabled={!canModifyData(currentUser)}
                              className="w-24 px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-400 disabled:bg-gray-100 disabled:cursor-not-allowed"
                            />
                            <span className="text-xs text-gray-500">{config.unit}</span>
                            <span className="text-xs text-gray-400 ml-auto">(default: {config.default})</span>
                          </div>
                        </div>
                      ))}
                      {modes.full && (
                      <div className="mt-2 pt-2 border-t border-gray-200">
                        <div className="flex items-center gap-2">
                          <span className="text-xs text-gray-400">🔒 Historical</span>
                          <span className="text-xs text-gray-500 italic">Always syncs all records</span>
                        </div>
                      </div>
                      )}
                    </div>
                    );
                  })}
                </div>
                {canModifyData(currentUser) && (
                  <div className="flex items-center justify-end gap-3 mt-4">
                    {settingsDirty && <span className="text-xs text-amber-600">Unsaved changes</span>}
                    <button
                      onClick={saveSyncSettings}
                      disabled={!settingsDirty || settingsSaving}
                      className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {settingsSaving ? 'Saving...' : 'Save Settings'}
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}
      </div>

      {/* Sync Cards */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <span className="inline-block w-8 h-8 border-4 border-gray-400 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-4 gap-6">
          <SyncCard entityType="customer" log={syncLogs.customer} isSyncing={syncing.customer} runningJob={runningJobs.customer} />
          <SyncCard entityType="order" log={syncLogs.order} isSyncing={syncing.order} runningJob={runningJobs.order} />
          <SyncCard entityType="contact" log={syncLogs.contact} isSyncing={syncing.contact} runningJob={runningJobs.contact} />
          <SyncCard entityType="user" log={syncLogs.user} isSyncing={syncing.user} runningJob={runningJobs.user} />
        </div>
      )}

      {/* Search Index (Typesense) */}
      <div className="mt-6">
        <div className="flex items-center gap-2 mb-3">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Search Index</h2>
          <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">Typesense</span>
        </div>
        <TypesenseCard log={syncLogs.typesense} isSyncing={syncing.typesense} runningJob={runningJobs.typesense} />
      </div>

      {/* Product Catalog (Quoting) */}
      <div className="mt-6">
        <div className="flex items-center gap-2 mb-3">
          <h2 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">Product Catalog</h2>
          <span className="text-xs text-gray-400 bg-gray-100 px-2 py-0.5 rounded-full">Quoting</span>
        </div>
        <ProductCatalogCard log={syncLogs.product} isSyncing={syncing.product} runningJob={runningJobs.product} />
      </div>

      {/* VPS Cron Info */}
      <div className="mt-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <span className="flex-shrink-0 mt-0.5">🕐</span>
          <div className="text-sm text-gray-700">
            <p className="font-semibold mb-2">Automated Sync Schedule (VPS Cron)</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="bg-white rounded p-3 border">
                <p className="font-medium text-gray-900">Customers</p>
                <p className="text-xs text-gray-600">Every 6 hours (2:00 AM ET)</p>
              </div>
              <div className="bg-white rounded p-3 border">
                <p className="font-medium text-gray-900">Orders</p>
                <p className="text-xs text-gray-600">Every 6 hours (1:00 AM ET)</p>
              </div>
              <div className="bg-white rounded p-3 border">
                <p className="font-medium text-gray-900">Contacts</p>
                <p className="text-xs text-gray-600">Every 6 hours (3:00 AM ET)</p>
              </div>
              <div className="bg-white rounded p-3 border">
                <p className="font-medium text-gray-900">Users</p>
                <p className="text-xs text-gray-600">Manual only</p>
              </div>
              <div className="bg-white rounded p-3 border">
                <p className="font-medium text-gray-900">Search Index</p>
                <p className="text-xs text-gray-600">Manual only (run after data syncs)</p>
              </div>
              <div className="bg-white rounded p-3 border">
                <p className="font-medium text-gray-900">Product Catalog</p>
                <p className="text-xs text-gray-600">Daily (3:30 AM ET)</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── Full Sync Confirmation Modal ── */}
      {confirmFullSync && (() => {
        const entity = confirmFullSync;
        const cursorEnd = cursorPositions[entity];
        const settingConfig = SYNC_SETTINGS[entity]?.full;
        const rewindVal = syncSettings[entity]?.full ?? settingConfig?.default;
        const isRecordsBased = settingConfig?.unit === 'records';
        const cursorAfterRewind = isRecordsBased && cursorEnd ? Math.max(1, cursorEnd - rewindVal) : null;
        return (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
            <div className="bg-white rounded-lg shadow-xl max-w-lg w-full mx-4 p-6">
              <div className="flex items-center gap-3 mb-4">
                <span className="text-xl">🗄️</span>
                <h3 className="text-lg font-semibold text-gray-900">Full Sync — {entity.charAt(0).toUpperCase() + entity.slice(1)}s</h3>
              </div>

              {/* Cursor context */}
              {cursorEnd && isRecordsBased && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4 text-sm">
                  <p className="font-medium text-blue-900 mb-2">📍 Current Cursor Position</p>
                  <div className="grid grid-cols-2 gap-2 text-blue-800">
                    <div>Last synced ID: <strong>#{cursorEnd}</strong></div>
                    <div>Re-scan setting: <strong>{rewindVal.toLocaleString()}</strong> records</div>
                    <div>Will start from: <strong>#{cursorAfterRewind}</strong></div>
                    <div>Coverage: <strong>#{cursorAfterRewind} → #{cursorEnd}</strong></div>
                  </div>
                </div>
              )}
              {!cursorEnd && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 mb-4 text-sm text-gray-600">
                  No previous sync cursor found — will scan from the beginning.
                </div>
              )}

              <p className="text-sm text-gray-700 mb-4">
                This will re-scan <strong>{isRecordsBased ? `~${rewindVal.toLocaleString()} records` : `the last ${rewindVal} days`}</strong> and
                write only actual changes. Adjust the re-scan range in <strong>⚙️ Sync Settings</strong> above.
              </p>

              {/* Optional: Start from specific ID */}
              {isRecordsBased && (
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3 mb-4">
                  <label className="block text-xs font-medium text-purple-900 mb-1">
                    Override: Start from specific ID <span className="text-gray-500 font-normal">(optional)</span>
                  </label>
                  <p className="text-xs text-purple-700 mb-2">
                    Enter a specific {entity} ID number to start scanning from. Leave empty to use the default re-scan range above.
                  </p>
                  <input
                    type="number"
                    min="1"
                    placeholder={`e.g. ${cursorAfterRewind || 1}`}
                    value={fullSyncStartId}
                    onChange={(e) => setFullSyncStartId(e.target.value)}
                    className="w-full px-3 py-2 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-purple-400"
                  />
                  {fullSyncStartId && cursorEnd && (
                    <p className="text-xs text-purple-600 mt-1">
                      Will scan: <strong>#{fullSyncStartId} → #{cursorEnd}</strong> (~{Math.max(0, cursorEnd - Number(fullSyncStartId)).toLocaleString()} records)
                    </p>
                  )}
                </div>
              )}

              <div className="flex gap-3 justify-end">
                <button onClick={() => setConfirmFullSync(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors">
                  Cancel
                </button>
                <button
                  onClick={() => {
                    const e = confirmFullSync;
                    const startId = fullSyncStartId ? Number(fullSyncStartId) : null;
                    setConfirmFullSync(null);
                    setFullSyncStartId('');
                    triggerSync(e, 'full', startId ? { startId } : {});
                  }}
                  className="px-4 py-2 text-sm font-medium text-white bg-purple-600 rounded hover:bg-purple-700 transition-colors"
                >
                  Start Full Sync
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── Historical Sync Confirmation Modal ── */}
      {confirmHistorical && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-center gap-3 mb-4">
              <span className="text-xl">⚠️</span>
              <h3 className="text-lg font-semibold text-gray-900">Historical Sync</h3>
            </div>
            <p className="text-sm text-gray-700 mb-4">
              This will scan <strong>all {confirmHistorical} records</strong> from ApparelMagic from the very beginning,
              comparing against existing Firestore data and writing only actual changes. This can take significantly longer than a normal sync.
            </p>
            <p className="text-sm text-amber-800 bg-amber-50 border border-amber-200 rounded p-3 mb-4">
              Use this when you suspect data drift — e.g., customers with stale status, outdated sales rep assignments, or records that haven't been updated in a long time.
            </p>
            <div className="flex gap-3 justify-end">
              <button onClick={() => setConfirmHistorical(null)} className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded hover:bg-gray-200 transition-colors">
                Cancel
              </button>
              <button
                onClick={() => { const e = confirmHistorical; setConfirmHistorical(null); triggerSync(e, 'historical'); }}
                className="px-4 py-2 text-sm font-medium text-white bg-amber-600 rounded hover:bg-amber-700 transition-colors"
              >
                Start Historical Sync
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Sync Detail Modal ── */}
      {detailModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 p-6 max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                {detailModal.entityType.charAt(0).toUpperCase() + detailModal.entityType.slice(1)} Sync — {detailModal.log.id}
              </h3>
              <button onClick={() => setDetailModal(null)} className="text-gray-500 hover:text-gray-700 text-lg">✕</button>
            </div>
            <div className="overflow-y-auto flex-1 space-y-4 text-sm">
              {/* Summary Grid */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {[
                  ['Status', detailModal.log.status === 'success' ? '✅ Success' : detailModal.log.status === 'crashed' ? '💥 Crashed' : detailModal.log.status === 'cancelled' ? '⏹ Cancelled' : '❌ Failed'],
                  ['Timestamp', formatTimestamp(detailModal.log.timestamp)],
                  ['Duration', formatDuration(detailModal.log.durationMs || detailModal.log.duration)],
                  ['Total Processed', normalizeSyncLog(detailModal.log).totalProcessed ?? 'N/A'],
                  ['Pages', normalizeSyncLog(detailModal.log).pages ?? 'N/A'],
                  ['Lookback / Re-scan', normalizeSyncLog(detailModal.log).lookback ?? 'N/A'],
                  ['Created', detailModal.log.stats?.created || 0],
                  ['Updated', detailModal.log.stats?.updated || 0],
                  ['Unchanged', detailModal.log.stats?.unchanged || 0],
                  ['Skipped', normalizeSyncLog(detailModal.log).skipped],
                  ['Cursor Start', normalizeSyncLog(detailModal.log).cursorStart ?? 'N/A'],
                  ['Cursor End', normalizeSyncLog(detailModal.log).cursorEnd ?? 'N/A'],
                ].map(([label, val]) => (
                  <div key={label} className="bg-gray-50 rounded p-2">
                    <span className="text-gray-500 text-xs">{label}</span>
                    <p className="font-medium text-gray-900">{String(val)}</p>
                  </div>
                ))}
              </div>

              {/* Error */}
              {detailModal.log.error && (
                <div className="bg-red-50 border border-red-200 rounded p-3">
                  <p className="font-semibold text-red-900 text-xs mb-1">Error</p>
                  <p className="text-red-800 text-xs whitespace-pre-wrap">{detailModal.log.error}</p>
                  {detailModal.log.loggedBy === 'syncServer' && (
                    <p className="text-red-600 text-xs mt-1 italic">Logged by sync server (script crashed before completing its own logging)</p>
                  )}
                </div>
              )}

              {detailModal.log.loggingWarning && (
                <div className="bg-yellow-50 border border-yellow-200 rounded p-3 text-xs text-yellow-900">
                  {detailModal.log.loggingWarning}
                </div>
              )}

              {/* Last stdout from crashed sync */}
              {detailModal.log.lastStdout && (
                <div className="bg-gray-50 border border-gray-200 rounded p-3">
                  <p className="font-semibold text-gray-700 text-xs mb-1">Last Output</p>
                  <pre className="text-gray-600 text-xs whitespace-pre-wrap max-h-32 overflow-auto font-mono">{detailModal.log.lastStdout}</pre>
                </div>
              )}

              {/* Updated Details */}
              {normalizeSyncLog(detailModal.log).updatedDetails.length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">
                    Updated Records ({normalizeSyncLog(detailModal.log).updatedDetailsTotal.toLocaleString()})
                    {normalizeSyncLog(detailModal.log).detailsTruncated && <span className="font-normal text-gray-500"> (showing first {normalizeSyncLog(detailModal.log).updatedDetails.length.toLocaleString()})</span>}
                  </h4>
                  <div className="border rounded overflow-auto max-h-48">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-100 sticky top-0">
                        <tr>
                          <th className="text-left px-3 py-1.5">ID</th>
                          <th className="text-left px-3 py-1.5">Name</th>
                          <th className="text-left px-3 py-1.5">Changed Fields</th>
                        </tr>
                      </thead>
                      <tbody>
                        {normalizeSyncLog(detailModal.log).updatedDetails.map((u, i) => (
                          <tr key={i} className="border-t">
                            <td className="px-3 py-1.5 font-mono">{u.id}</td>
                            <td className="px-3 py-1.5">{u.name}</td>
                            <td className="px-3 py-1.5 text-blue-700">{u.changedFields.join(', ')}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Created IDs */}
              {normalizeSyncLog(detailModal.log).createdIds.length > 0 && (
                <div>
                  <h4 className="font-semibold text-gray-800 mb-2">
                    Created Records ({normalizeSyncLog(detailModal.log).createdIdsTotal.toLocaleString()})
                    {normalizeSyncLog(detailModal.log).detailsTruncated && <span className="font-normal text-gray-500"> (showing first {normalizeSyncLog(detailModal.log).createdIds.length.toLocaleString()})</span>}
                  </h4>
                  <p className="text-xs text-gray-600">{normalizeSyncLog(detailModal.log).createdIds.join(', ')}</p>
                </div>
              )}

              {!normalizeSyncLog(detailModal.log).detailsRecorded
                && ((detailModal.log.stats?.created || 0) + (detailModal.log.stats?.updated || 0)) > 0 && (
                <p className="text-xs text-gray-500 italic">
                  {detailModal.log.loggingWarning || 'Per-record details were not recorded for this run (logged by an older sync script version).'}
                </p>
              )}
            </div>

            {/* Footer Actions */}
            <div className="flex gap-2 justify-end pt-4 border-t mt-4">
              <button onClick={() => exportSyncLog(detailModal.entityType, detailModal.log, 'csv')} className="px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 rounded transition-colors">
                ⬇️ CSV
              </button>
              <button onClick={() => exportSyncLog(detailModal.entityType, detailModal.log, 'xlsx')} className="px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 rounded transition-colors">
                ⬇️ Excel
              </button>
              <button onClick={() => setDetailModal(null)} className="px-4 py-1.5 text-xs bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors">
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Sync History Modal ── */}
      {historyModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-lg shadow-xl max-w-3xl w-full mx-4 p-6 max-h-[80vh] flex flex-col">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                📜 {historyModal.charAt(0).toUpperCase() + historyModal.slice(1)} Sync History (Last 25)
              </h3>
              <button onClick={() => setHistoryModal(null)} className="text-gray-500 hover:text-gray-700 text-lg">✕</button>
            </div>

            {historyLoading ? (
              <div className="flex items-center justify-center py-12">
                <span className="inline-block w-6 h-6 border-2 border-gray-400 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <div className="overflow-auto flex-1">
                <table className="w-full text-xs">
                  <thead className="bg-gray-100 sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2">Run ID</th>
                      <th className="text-left px-3 py-2">Status</th>
                      <th className="text-left px-3 py-2">Timestamp</th>
                      <th className="text-right px-3 py-2">Duration</th>
                      <th className="text-right px-3 py-2">Created</th>
                      <th className="text-right px-3 py-2">Updated</th>
                      <th className="text-right px-3 py-2">Unchanged</th>
                      <th className="text-center px-3 py-2">Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyLogs.map((l) => (
                      <tr key={l.id} className="border-t hover:bg-gray-50">
                        <td className="px-3 py-2 font-mono truncate max-w-[120px]" title={l.id}>{l.id}</td>
                        <td className="px-3 py-2">{l.status === 'success' ? '✅' : l.status === 'crashed' ? '💥' : l.status === 'cancelled' ? '⏹' : '❌'}</td>
                        <td className="px-3 py-2">{formatTimestamp(l.timestamp)}</td>
                        <td className="px-3 py-2 text-right">{formatDuration(l.durationMs || l.duration)}</td>
                        <td className="px-3 py-2 text-right text-green-600">{l.stats?.created || 0}</td>
                        <td className="px-3 py-2 text-right text-blue-600">{l.stats?.updated || 0}</td>
                        <td className="px-3 py-2 text-right text-gray-500">{l.stats?.unchanged || 0}</td>
                        <td className="px-3 py-2 text-center">
                          <button
                            onClick={() => { setHistoryModal(null); setDetailModal({ entityType: historyModal, log: l }); }}
                            className="text-blue-600 hover:underline"
                          >
                            View
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Footer */}
            <div className="flex gap-2 justify-end pt-4 border-t mt-4">
              <button onClick={() => exportHistory(historyModal, historyLogs, 'csv')} disabled={!historyLogs.length} className="px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 rounded disabled:opacity-50 transition-colors">
                ⬇️ Export CSV
              </button>
              <button onClick={() => exportHistory(historyModal, historyLogs, 'xlsx')} disabled={!historyLogs.length} className="px-3 py-1.5 text-xs bg-gray-100 hover:bg-gray-200 rounded disabled:opacity-50 transition-colors">
                ⬇️ Export Excel
              </button>
              <button onClick={() => setHistoryModal(null)} className="px-4 py-1.5 text-xs bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors">
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
